import { createSlice } from "@reduxjs/toolkit";
import {
  registerBuilder,
  logoutBuilder,
  refreshBuilder,
  loginBuilder,
  getProfileBuilder,
  getMeBuilder,
  updateProfileBuilder,
  changePasswordBuilder,
  getInfoAboutAdminBuilder,
  forgotPasswordBuilder,
  resetPasswordBuilder,
} from "./builder";
import { LOGOUT } from "../../../utils/constants";
import { storageUtils } from "../../../utils/storage";

// Get initial state from storage (localStorage or sessionStorage)
const getInitialState = () => {
  try {
    const token = storageUtils.getAuthData('accessToken');
    const user = storageUtils.getAuthData('user');
    
    return {
      email: user?.email || "",
      loggedIn: token && user ? true : false,
      token: token || null,
      user: user,
      loginPending: false,
      loading: false,
      error: null,
    };
  } catch (error) {
    // If there's an error parsing storage, return default state
    return {
      email: "",
      loggedIn: false,
      token: null,
      user: null,
      loginPending: false,
      loading: false,
      error: null,
    };
  }
};

const initialState = getInitialState();

export const authSlice = createSlice({
  name: "authInfo",
  initialState,
  reducers: {
    setLoggedIn: (state, { payload }) => {
      state.loggedIn = payload.loggedIn;
      state.isAuthenticated = payload.loggedIn;
    },
    setAccessToken: (state, { payload }) => {
      const { accessToken, rememberMe = false } = payload;
      state.token = accessToken;
      
      // Update token in appropriate storage
      if (accessToken) {
        storageUtils.setAuthData('accessToken', accessToken, rememberMe);
      }
    },
    setUser: (state, { payload }) => {
      const { user, rememberMe = false } = payload;
      state.user = user;
      state.email = user?.email || "";
      
      // Update user in appropriate storage
      if (user) {
        storageUtils.setAuthData('user', user, rememberMe);
      }
    },
  },
  extraReducers: (builder) => {
    registerBuilder(builder);
    logoutBuilder(builder);
    loginBuilder(builder);
    refreshBuilder(builder);
    getProfileBuilder(builder);
    getMeBuilder(builder);
    updateProfileBuilder(builder);
    changePasswordBuilder(builder);
    getInfoAboutAdminBuilder(builder);
    forgotPasswordBuilder(builder);
    resetPasswordBuilder(builder);

    builder.addCase(LOGOUT, () => ({
      ...initialState,
      loggedIn: false,
    }));
  },
});

export const { setLoggedIn, setAccessToken, setUser } = authSlice.actions;

export const selectLoggedIn = (state) => state.authInfo.loggedIn;
export const selectToken = (state) => state.authInfo.token;
export const selectUser = (state) => state.authInfo.user;
export const selectUserRole = (state) => state.authInfo.user?.role;
export const selectIsAdmin = (state) => state.authInfo.user?.role === 'admin';
export const selectIsUser = (state) => state.authInfo.user?.role === 'user';
export const selectEmailVerified = (state) => state.authInfo.user?.email_verified || state.authInfo.user?.verified;
export const selectLoginPending = (state) => state.authInfo.loginPending;
export const selectEmail = (state) => state.authInfo.email;
export const selectLoading = (state) => state.authInfo.loading;
export const selectError = (state) => state.authInfo.error;

export default authSlice.reducer;
